<template>
  <div :class="[`auth`, formId]">
    <h1 
      v-if="$route.name === `register`" 
      :style="{ 'color': org.custom_color1 == null ? '': org.custom_color1 }"
      class="h1 group__title mw600 mb0">
      {{
        f2fSelected
          ? `Submit your in-person appointment request`
          : queryData
            ? `Please register to continue`
            : `Let's book your appointment`
      }}
    </h1>
    <p
      v-if="$route.name === `register` && id === `register`"
      class="p group__desc mb0"
    >
      Create an account to submit your request.
    </p>
    <p
      v-if="$route.name === `register` && id === `register`"
      class="p group__desc mb2 mt0"
    >
      Already have an account?
      <span @click="redirectToLogin" class="link">Login</span>
    </p>

    <div class="mb">
      <h2 
        v-if="auth.title" 
        :style="{ 'color': org.custom_color1 == null ? '': org.custom_color1 }"
        class="h2 mb0">{{ auth.title }}</h2>
      <p v-if="auth.copy && showForm" class="p">
        {{
          isRescheduling
            ? "You are rescheduling your appointment, we already have all you details, enter you initials below to confim your appointment"
            : auth.copy
        }}
      </p>
    </div>

    <!-- Form -->
    <module-form
      v-show="showForm"
      :id="formId"
      :action="action"
      :success="onSuccess"
      :fail="onError"
    >
      <!-- Fields -->
      <module-fieldset
        v-for="(field, fieldId) in auth.fields || {}"
        :key="fieldId"
        :id="fieldId"
        v-bind="field"
      />

      <!-- Server Error -->
      <module-error :id="formId" />

      <!-- Submit -->
      <module-button
        :form="formId"
        :disabled="!(getForm(formId) || {}).valid"
        button="primary"
        v-bind="button"
      />
      <module-button
        type="button"
        v-if="id === 'verify'"
        @click.native="sendVerification"
      >
        {{ verifyText }}
      </module-button>
    </module-form>

    <template v-if="!showForm">
      <template v-if="id === `confirm`">
        <p class="medium">You can now log in with your new password.</p>
        <module-button label="Log in" link="login" />
      </template>
    </template>

    <ui-module-loader
      key="loader"
      v-if="showRescheduleLoader === true"
      :messages="['Please give us a moment to reschedule']"
    />
  </div>
</template>

<script>
// Vuex
import { mapGetters } from 'vuex';

// Models
import auth, { register } from 'models/auth';

// Modules
import UiModuleLoader from 'components/ModuleLoader';

// Export
export default {
  name: 'view-login',

  // Components
  components: {
    UiModuleLoader,
  },

  // Props
  props: {
    id: {
      type: String,
      required: true,
    },
    success: {
      type: Function,
    },
    fail: {
      type: Function,
    },
    sendVerification: {
      type: Function,
    },
    verifyText: String,
  },

  // Data

  data() {
    return {
      showRescheduleLoader: false,
    };
  },

  computed: {
    ...mapGetters('forms', ['getFormView', 'getForm', 'getField']),
    ...mapGetters('auth', ['user', 'verified', 'tempEmail']),
    ...mapGetters('questions', ['f2fSelected']),
    ...mapGetters('appointment', ['appointments', 'queryData']),
    ...mapGetters('provider', ['provider']),
    ...mapGetters('app', ['appState', 'messagesOverride', 'org']),

    isRescheduling() {
      return localStorage.getItem('rescheduling');
    },

    formId() {
      return `auth-${this.id}`;
    },
    formView() {
      return this.getFormView(this.formId);
    },
    formSuccess() {
      return (this.getForm(this.formId) || {}).state === 'success';
    },
    auth() {
      return auth[this.id] || {};
    },
    showForm() {
      if (this.id === 'confirm') return !this.formSuccess;

      return true;
    },
    button() {
      if (this.auth && this.auth.button) {
        if (this.isRescheduling) {
          return {
            label: 'Reschedule Appointment',
          };
        } else if (this.id === 'login') {
          return {
            label: this.queryData
              ? 'Log in & send request'
              : this.auth.button.label,
            hidden: this.verified === 'initial' ? false : !this.verified,
          };
        } else if (
          this.id === 'verify' &&
          this.$route.name !== 'register-verification' &&
          !this.$route.name === 'auth-reset'
        ) {
          return {
            ...this.auth.button,
            hidden: this.verified === 'initial' ? true : this.verified,
          };
        } else if (
          this.$route.name === 'auth-reset' &&
          this.id === 'reset' &&
          !this.verified
        ) {
          return {
            hidden: true,
          };
        } else if (
          this.$route.name === 'auth-reset' &&
          this.id === 'confirm' &&
          this.verified
        ) {
          return {
            hidden: true,
          };
        } else if (this.formId === 'auth-register') {
          return this.queryData
            ? { label: 'Submit request' }
            : this.auth.button;
        }

        return this.auth.button;
      }
    },
  },

  // Methods
  methods: {
    action() {
      const data = {};

      _.forEach(this.formView, (value, key) => {
        // eslint-disable-line no-undef
        data[key] = Array.isArray(value) ? value[0] : value;
      });

      if (
        (this.formId === 'auth-verify' && this.user.email) ||
        (this.getFormView('auth-login') && this.getFormView('auth-login').email)
      ) {
        data.email = this.user.email || this.getFormView('auth-login').email;
      }

      if (
        this.id === 'verify' &&
        this.$route.name !== 'register-verification' &&
        this.$route.name !== 'login'
      ) {
        data.email = sessionStorage.getItem('auth-reset|email');
      }

      if (this.id === 'registerbooking') {
        if (localStorage.getItem('rescheduleId')) {
          data.appointment = localStorage.getItem('rescheduleId');
        } else if (this.appointments.length > 0) {
          data.appointment = this.appointments[0].id;
        }

        return this.$store.dispatch(`appointment/${this.auth.action}`, data);
      }

      if (this.id === 'confirm') {
        data.email = this.tempEmail;
        data.otp = this.getFormView('auth-verify').otp;
      }

      if (this.id === 'resetPassword') {
        data.otp = this.getFormView('auth-resetPassword').otp2;
        data.email = this.tempEmail;
        delete data.otp2; // otp was already used an gave a duplicate error so I added a secon one
      }

      if (this.id === 'appointmentQuery') {
        return this.$store.dispatch(`appointment/${this.auth.action}`, data);
      }

      return this.$store.dispatch(`auth/${this.auth.action}`, data);
    },
    onSuccess(success) {
      if (this.success) this.success(success);
    },
    onError(error) {
      if (this.fail) this.fail(error);
    },

    redirectToLogin() {
      localStorage.setItem('loginOvertake', true);
      this.$router.push({ name: 'login' });
    },
  },

  created() {
    const buildLocation = [];
    const locations = this.org.child_organisations || [];

    if (!localStorage.getItem('fueledAuthToken')) {
      locations.forEach(item => {
        buildLocation.push({
          id: item.id,
          location: item.location,
        });
      });

      if (locations.length > 0) {
        register.fields.org_location.options = buildLocation;
      } else {
        delete register.fields.org_location;
      }
    }
  },

  beforeDestroy() {
    this.showRescheduleLoader = false;
  },
};
</script>



// WEBPACK FOOTER //
// Auth.vue?a88075e4